import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Modal, Table, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import UspStrip from '../components/UspStrip';
import Footer from '../components/Footer';

interface ServiceDetail {
  title: string;
  description: string;
  icon: string;
  machinery: string;
  materials: string;
  dimensions: string;
  bestFor: string;
  detailsList: string[];
}

const servicesList: ServiceDetail[] = [
  {
    title: 'High-Speed Digital Press',
    description: 'Short-run, high-precision print production on industry-leading laser presses. Perfect for brochures, flyers, booklets, and custom cards.',
    icon: 'fas fa-print',
    machinery: 'Konica Minolta AccurioPress C4070 / C3070 series',
    materials: '130 GSM Gloss/Matte, 170 GSM Premium Gloss, 300 GSM Heavy Cardstock, Textured Kraft, Eco-friendly stocks',
    dimensions: 'Up to 13" x 19" sheet size (Max print area: 12.6" x 18.7")',
    bestFor: 'Visiting cards, flyers, corporate brochures, menus, milestone certificates',
    detailsList: [
      'Stunning high-resolution color consistency (3600 x 2400 dpi equivalent)',
      'Variable data printing (VDP) for unique serial numbers or individual names',
      'Fast same-day turnaround for digital files',
      'Eco-friendly polymer toners with zero ozone discharge'
    ]
  },
  {
    title: 'Large Format Flex & Signage',
    description: 'Vibrant large-scale outdoor signage, billboards, lightboxes, backlit banners, and vinyl event boards designed to make a massive visual statement.',
    icon: 'fas fa-image',
    machinery: 'Dual-head high-speed Eco-Solvent and Solvent Large Format Printers',
    materials: 'Standard Flex (Economical 280 GSM), Star Flex (Premium thick 340 GSM), Backlit Flex (Optimum light diffusion for lightboxes)',
    dimensions: 'Up to 10ft width rolls by infinite length (Panel stitching available for larger billboards)',
    bestFor: 'Storefront signage, event backgrounds, billboards, lightboxes, corporate exhibition boards',
    detailsList: [
      'Waterproof and UV-resistant outdoor durability for up to 3 years',
      'High-saturation inks ensuring colors do not fade under direct Chennai sun',
      'Various mounting support: rust-proof eyelets (grommets), pocket folds, and wooden frame backing',
      'Available in frontlit and backlit grades'
    ]
  },
  {
    title: 'Stationery & Custom Invites',
    description: 'Luxury cards, envelope sets, illustrations, and premium invitations designed with special foil layers and textures for unforgettable occasions.',
    icon: 'fas fa-envelope-open-text',
    machinery: 'Semi-Automatic Thermal Foil Stamping Press & Spot UV curing chamber',
    materials: 'Handmade textured boards, metallic cardstock, linen finish cards, premium cotton papers',
    dimensions: 'Custom shapes and standard leaf templates (A5, A6, Gatefold)',
    bestFor: 'Wedding invitations, luxury event RSVP cards, VIP corporate invite cards, customized envelopes',
    detailsList: [
      'Premium Hot Foil Stamping available in Gold, Silver, Rose Gold, and Copper',
      'Spot UV coating to give text or specific design outlines a raised, glossy 3D texture',
      'Custom die-cut shapes (gates, windows, arcs) to create interactive card structures',
      'Matching branded envelopes crafted from corresponding paper stock weight'
    ]
  },
  {
    title: 'Corporate Branding & Merchandise',
    description: 'Elevate your corporate brand identity with custom plastic employee ID badges, access smartcards, logo lanyards, custom plaques, and photo frames.',
    icon: 'fas fa-id-card',
    machinery: 'High-definition thermal dye-sublimation ID printers & Flatbed UV merchandise presses',
    materials: 'Solid PVC cards (0.76mm), RFID access chips, high-grade wood/acrylic frames, sublimated polyester ribbon',
    dimensions: 'Standard CR80 size (85.6mm x 54mm) for ID cards, customizable dimensions for plaques and frames',
    bestFor: 'Employee identification, access keycards, corporate plaques, custom-sized photo frames',
    detailsList: [
      'Dual-sided full-color thermal print with a clear protective overlay against scratches',
      'Smart RFID integration (125KHz or 13.56MHz frequencies) for office biometric scanners',
      'Branded logo lanyards in 12mm and 20mm widths with heat-transferred graphic text',
      'Premium photo frame borders in classic black wood, oak wood, and golden metal finishes'
    ]
  },
  {
    title: 'Bespoke Designing & Prepress',
    description: 'Creative design studio services to craft logos, layout templates, typography schemas, and files fully print-ready with accurate bleed margins.',
    icon: 'fas fa-paint-brush',
    machinery: 'Adobe Creative Suite Workstations (Illustrator, Photoshop, InDesign)',
    materials: 'High-resolution digital outputs mapped to specific CMYK ink profiles',
    dimensions: 'Custom specifications adapted to all standard international paper templates (A-series, B-series)',
    bestFor: 'Startups, logo design, prepress bleed adjustment, menu layouts, promotional leaflets',
    detailsList: [
      'Expert design consultations to ensure design typography and color choices match print paper characteristics',
      'Bleed margin calibration and image resolution enhancement to avoid blurry prints',
      'CMYK color alignment to ensure screen designs exactly replicate on final print papers',
      'Delivery of high-resolution vector PDF, EPS, and SVG source files'
    ]
  },
  {
    title: 'Post-Press & Binding Services',
    description: 'Provide clean professional finishes to text documents, newsletters, brochures, and menu lists using high-quality laminations, folds, and bindings.',
    icon: 'fas fa-book-open',
    machinery: 'Heavy-duty hydraulic paper cutters, thermal laminators, and multi-mode folding machines',
    materials: 'Velvet soft-touch lamination films, anti-scratch matte films, high-gloss UV laminates, steel wire rings',
    dimensions: 'Sheet handling from pocket menu sizes up to A3 booklet specifications',
    bestFor: 'Restaurant menu booklets, corporate portfolios, textbooks, bi-fold catalogs, tri-fold leaflets',
    detailsList: [
      'Clean post-press folding: Bi-fold, Tri-fold, Z-Fold, and Accordion folders',
      'Binding options: Saddle-stitch (stapled), Spiral coil (wire-O), and perfect hot-melt bookbinding',
      'Lamination overlays (Gloss/Matte) that protect cardstocks from water spills and tears',
      'Precision hydraulic trimming guaranteeing clean borders on all print sheets'
    ]
  }
];

const ServicesPage: React.FC = () => {
  const [selectedService, setSelectedService] = useState<ServiceDetail | null>(null);

  return (
    <div className="page-fade d-flex flex-column" style={{ minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ flex: 1 }}>
        
        {/* Breadcrumbs */}
        <div className="breadcrumb-bar">
          <Link to="/">Home</Link> &gt; Services
        </div>

        {/* Hero Section */}
        <div className="portfolio-hero text-center py-5" style={{ background: 'linear-gradient(135deg, #fff5fb 0%, #fdf2f8 50%, #ffffff 100%)', position: 'relative', overflow: 'hidden' }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(233, 30, 140, 0.05)',
            zIndex: 0
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '-80px',
            left: '-80px',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(59, 91, 219, 0.05)',
            zIndex: 0
          }}></div>

          <Container style={{ position: 'relative', zIndex: 1 }}>
            <h2 className="display-5 fw-bold text-dark" style={{ 
              letterSpacing: '-0.5px',
              fontSize: 'clamp(28px, 6vw, 48px)',
              marginBottom: '20px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #3b5bdb 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Our <span style={{ background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Services</span>
            </h2>
            <div style={{ 
              width: '70px', 
              height: '4px', 
              background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)', 
              borderRadius: '2px',
              boxShadow: '0 4px 12px rgba(233, 30, 140, 0.3)',
              margin: '0 auto 32px'
            }}></div>
            <p className="lead text-muted mx-auto mb-5" style={{ 
              maxWidth: '680px', 
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              lineHeight: '1.7',
              color: '#555555',
              fontWeight: 500
            }}>
              Explore our comprehensive range of commercial printing capabilities, custom corporate branding systems, and professional prepress studio layouts.
            </p>
            <a
              href="https://wa.me/919150808130"
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary-custom fw-bold text-white d-inline-flex align-items-center gap-2 text-decoration-none"
              style={{ 
                borderRadius: '10px', 
                fontSize: 'clamp(13px, 2.5vw, 15px)',
                padding: '14px 28px',
                background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                border: 'none',
                boxShadow: '0 6px 20px rgba(233, 30, 140, 0.35)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                cursor: 'pointer',
                fontWeight: 700,
                letterSpacing: '0.3px'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(233, 30, 140, 0.45)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(233, 30, 140, 0.35)';
              }}
            >
              <i className="fab fa-whatsapp"></i> Chat on WhatsApp
            </a>
          </Container>
        </div>

        {/* Services Grid Section */}
        <div style={{ background: 'var(--bg-secondary)', padding: '60px 5%' }}>
          <Container>
            <Row className="g-4">
              {servicesList.map((service, index) => (
                <Col key={index} xs={12} md={6} lg={4}>
                  <Card
                    onClick={() => setSelectedService(service)}
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                      border: '1px solid #eaeef5',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget;
                      el.style.transform = 'translateY(-8px)';
                      el.style.boxShadow = '0 12px 25px rgba(233, 30, 140, 0.12)';
                      el.style.borderColor = 'rgba(233, 30, 140, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget;
                      el.style.transform = 'translateY(0)';
                      el.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.05)';
                      el.style.borderColor = '#eaeef5';
                    }}
                  >
                    <Card.Body className="p-4 d-flex flex-column h-100">
                      {/* Icon Wrap */}
                      <div 
                        className="rounded-3 d-flex align-items-center justify-content-center mb-3"
                        style={{ 
                          width: '48px', 
                          height: '48px', 
                          fontSize: '20px',
                          background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.1), rgba(59, 91, 219, 0.1))',
                          color: '#e91e8c'
                        }}
                      >
                        <i className={service.icon}></i>
                      </div>
                      
                      <Card.Title className="fw-bold text-dark mb-2" style={{ fontSize: '18px', letterSpacing: '-0.3px' }}>
                        {service.title}
                      </Card.Title>
                      
                      <Card.Text className="text-muted mb-4" style={{ fontSize: '13.5px', lineHeight: '1.6', flexGrow: 1 }}>
                        {service.description}
                      </Card.Text>

                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="fw-bold align-self-start py-2 px-3 border-0 bg-light-row"
                        style={{ 
                          borderRadius: '8px', 
                          fontSize: '12px',
                          color: 'var(--pink)',
                          backgroundColor: 'rgba(233, 30, 140, 0.06)'
                        }}
                      >
                        View Specifications <i className="fas fa-arrow-right ms-1"></i>
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </div>

        {/* Dynamic Detail Modal */}
        <Modal 
          show={selectedService !== null} 
          onHide={() => setSelectedService(null)} 
          size="lg" 
          centered
        >
          {selectedService && (
            <>
              <Modal.Header closeButton className="border-bottom-0 pb-0">
                <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
                  <div 
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{ 
                      width: '38px', 
                      height: '38px', 
                      fontSize: '16px',
                      background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.1), rgba(59, 91, 219, 0.1))',
                      color: '#e91e8c'
                    }}
                  >
                    <i className={selectedService.icon}></i>
                  </div>
                  {selectedService.title} Specifications
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="px-4 pb-4 pt-3">
                <p className="text-muted mb-4" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  {selectedService.description}
                </p>

                {/* Technical Specifications Table */}
                <h6 className="fw-bold text-dark mb-2.5 border-bottom pb-2">Technical Capabilities</h6>
                <Table bordered hover responsive style={{ fontSize: '13px' }} className="mb-4 align-middle">
                  <tbody>
                    <tr>
                      <td className="fw-bold bg-light" style={{ width: '150px' }}>Machinery Used</td>
                      <td className="text-dark font-monospace">{selectedService.machinery}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold bg-light">Paper & Material Stocks</td>
                      <td className="text-dark">{selectedService.materials}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold bg-light">Dimensions</td>
                      <td className="text-dark">{selectedService.dimensions}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold bg-light">Best Suited For</td>
                      <td className="text-dark">
                        {selectedService.bestFor.split(', ').map((item, idx) => (
                          <Badge key={idx} bg="secondary" className="me-1 px-2.5 py-1.5 rounded-3 fw-medium text-capitalize" style={{ fontSize: '10.5px' }}>
                            {item}
                          </Badge>
                        ))}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Quality Standards list */}
                <h6 className="fw-bold text-dark mb-2.5 border-bottom pb-2">Key Quality Features</h6>
                <ul className="d-flex flex-column gap-2 p-0 m-0" style={{ listStyleType: 'none' }}>
                  {selectedService.detailsList.map((item, idx) => (
                    <li key={idx} className="d-flex align-items-start gap-2.5 text-muted" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                      <i className="fas fa-check-circle text-success mt-0.5" style={{ fontSize: '14px', flexShrink: 0 }}></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Modal.Body>
              <Modal.Footer className="border-top-0 pt-0 px-4 pb-4">
                <Button 
                  variant="secondary" 
                  onClick={() => setSelectedService(null)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                >
                  Close
                </Button>
                <Link 
                  to="/products"
                  className="btn btn-primary-custom py-2 px-3 text-white text-decoration-none fw-bold"
                  style={{ borderRadius: '8px', fontSize: '13px', background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)' }}
                  onClick={() => setSelectedService(null)}
                >
                  View Pricing
                </Link>
              </Modal.Footer>
            </>
          )}
        </Modal>

      </div>
      <UspStrip />
      <Footer />
    </div>
  );
};

export default ServicesPage;
